import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CraProductDetail, CraRelease, CraVulnerability } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { FinestraVex, TabellaVex, etichettaStatoVex, type SoggettoVex } from '../components/CancelloSicurezza'
import { Card, ErrorBanner, PageSection, Spinner } from '../components/common'
import { CriticitaBadge } from '../components/BusinessApps'
import { etichettaCategoria, etichettaRuolo, FontiSfruttata, giornoCra } from '../components/Cra'
import { FinestraNuovaSegnalazione, FinestraProdotto, FinestraRilascio } from '../components/CraFinestre'
import { DataTable, type Column } from '../components/DataTable'
import { nonLicenziato, scaricaJson } from '../components/Sbom'
import { BadgeGravita } from '../components/SoftwareProdotto'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { TabellaRequisiti, TabellaSegnalazioni } from './Cra'

/**
 * One product of the CRA register: its card (scope, role, support period,
 * contact), its releases with their SBOM (proposed from the tags), the
 * vulnerabilities of the supported releases with who says they are
 * exploited and the VEX that speaks about them, its reports, its
 * requirements. See cra_service.
 */

const gravita = (s: string) => ({ critica: 'critical', alta: 'high', media: 'medium', bassa: 'low' } as Record<string, string>)[s] ?? s

export default function CraProdotto() {
  const { id = '' } = useParams()
  const dati = useAsync(() => api.craProduct(id), [id])
  const p = dati.data
  if (dati.error && nonLicenziato(dati.error)) return <Card><p>{t('Non compreso nella licenza')}</p></Card>
  return (
    <>
      <header className="page__header">
        <div>
          <p className="field__hint" style={{ margin: 0 }}><Link to="/cra?vista=prodotti">{t('CRA Compliance')} · {t('Prodotti')}</Link></p>
          <h1 className="page__title">{p?.name ?? t('Prodotto')}</h1>
          {p && (
            <p className="page__subtitle">
              <span className={`badge ${p.in_scope ? 'badge--info' : 'badge--muted'}`}>{p.in_scope ? t('nel campo del CRA') : t('fuori dal CRA')}</span>{' '}
              {etichettaRuolo(p.role)} · {etichettaCategoria(p.category)}
              {' · '}{p.criticality ? <CriticitaBadge value={p.criticality} /> : <small className="text-muted">{t('criticità da definire')}</small>}
              {p.business_app_name && <small className="text-muted"> ({t('Business App «{nome}»', { nome: p.business_app_name })}{p.criticality_source === 'override' ? `, ${t('scelta a mano')}` : ''})</small>}
            </p>
          )}
        </div>
      </header>
      <ErrorBanner error={dati.error} />
      {dati.loading && !p ? <Spinner /> : p && <Dettaglio p={p} onCambiato={() => void dati.reload()} />}
    </>
  )
}

function Dettaglio({ p, onCambiato }: { p: CraProductDetail; onCambiato: () => void }) {
  const scrive = usePermessiModuli().scrive('cra')
  const naviga = useNavigate()
  const [scheda, setScheda] = useState(false)
  const [rilascio, setRilascio] = useState(false)
  const [segnalazione, setSegnalazione] = useState(false)
  const [vex, setVex] = useState<SoggettoVex | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function proposta(sbomId: string) {
    setErrore(null)
    try { await api.craReleaseAdd(p.id, { sbom_id: sbomId }); onCambiato() } catch (e) { setErrore(e) }
  }

  async function elimina() {
    if (!window.confirm(t('Il prodotto e i suoi rilasci si cancellano dal registro. Continuare?'))) return
    try { await api.craProductDelete(p.id); naviga('/cra?vista=prodotti') } catch (e) { setErrore(e) }
  }

  async function apriDaRilevazione(v: CraVulnerability) {
    setErrore(null)
    const d = p.detections.find((x) => x.vuln_id === v.vuln_id && x.component === v.component && !x.resolved_at)
    try {
      const r = await api.craReportOpen(d ? { product_id: p.id, kind: 'vulnerability', detection_id: d.id }
        : { product_id: p.id, kind: 'vulnerability', title: `${v.vuln_id} in ${v.component}`, vuln_id: v.vuln_id,
          aliases: v.aliases, release_ids: v.release_ids })
      naviga(`/cra/reports/${r.id}`)
    } catch (e) { setErrore(e) }
  }

  return (
    <>
      <ErrorBanner error={errore} />
      <div className="toolbar">
        <button type="button" disabled={!scrive} onClick={() => setScheda(true)}>{t('Modifica la scheda')}</button>
        <button type="button" className="secondary" disabled={!scrive || !p.in_scope} onClick={() => setSegnalazione(true)}>{t('Nuova segnalazione')}</button>
        <button type="button" className="secondary" disabled={!scrive || p.reports.length > 0} onClick={() => void elimina()}
          title={p.reports.length ? t('Ha delle segnalazioni: si mette fuori ambito, non si cancella') : undefined}>{t('Elimina')}</button>
      </div>
      <PageSection id="scheda">
        <Card title={t('Scheda')}>
          <dl className="kv">
            {!p.in_scope && <><dt>{t('Perché fuori dal CRA')}</dt><dd>{p.scope_reason}</dd></>}
            <dt>{t('Supporto fino al')}</dt><dd>{p.support_until ? giornoCra(p.support_until) : <span className="badge badge--warn">{t('da definire')}</span>}{p.support_reason && <> · <small>{p.support_reason}</small></>}</dd>
            <dt>{t('Punto di contatto per le vulnerabilità')}</dt><dd>{p.vuln_contact || <span className="badge badge--warn">{t('manca')}</span>}</dd>
            <dt>{t('Politica di divulgazione coordinata')}</dt><dd>{p.cvd_policy_url || '—'}</dd>
            <dt>{t('CSIRT coordinatore')}</dt><dd>{p.csirt || '—'} · {t('Stati membri')}: {p.member_states.join(', ') || '—'}</dd>
            <dt>{t('Responsabile')}</dt><dd>{p.owner_email || '—'}</dd>
            {(p.role === 'importer' || p.role === 'distributor') && <><dt>{t('Fabbricante da informare')}</dt><dd>{p.manufacturer_name || '—'} · {p.manufacturer_contact || '—'}</dd></>}
            <dt>{t('Rilasci dai tag')}</dt><dd>{p.release_rule === 'tag' ? t('ogni tag con SBOM diventa un rilascio') : t('i tag sono proposti')}</dd>
          </dl>
        </Card>
      </PageSection>
      <PageSection id="rilasci"><Rilasci p={p} scrive={scrive} onCambiato={onCambiato} onAggiungi={() => setRilascio(true)} onProposta={(s) => void proposta(s)} /></PageSection>
      <PageSection id="vulnerabilita">
        <Card title={t('Vulnerabilità nei rilasci supportati')}>
          <p className="field__hint" style={{ marginTop: 0 }}>{t('Dagli SBOM dei rilasci supportati, valutati con lo stesso motore del resto di CENSO. «Sfruttata» vuol dire nel catalogo CISA KEV o nell’elenco ENISA EUVD: è ciò che obbliga alla segnalazione.')}</p>
          <TabellaVulnerabilita righe={p.vulnerabilities} scrive={scrive && p.in_scope} onValuta={(v) => setVex({
            vuln_id: v.vuln_id, aliases: v.aliases, ecosystem: v.ecosystem, name: v.component, purl_base: v.purl_base,
            version: v.versions.length === 1 ? v.versions[0] : '' })} onSegnala={(v) => void apriDaRilevazione(v)}
            segnalate={new Set(p.detections.filter((d) => d.report_id).map((d) => `${d.vuln_id}|${d.component}`))} />
        </Card>
      </PageSection>
      <PageSection id="segnalazioni"><Card title={t('Segnalazioni')}><TabellaSegnalazioni righe={p.reports} conProdotto={false} /></Card></PageSection>
      <PageSection id="requisiti"><Card title={t('Requisiti')}><TabellaRequisiti righe={p.requirements} /></Card></PageSection>
      <PageSection id="vex"><Card title={t('Valutazioni (VEX)')}><TabellaVex righe={p.vex} scrive={scrive} onCambiato={onCambiato} /></Card></PageSection>
      {scheda && <FinestraProdotto attuale={p} onChiudi={() => setScheda(false)} onFatto={() => { setScheda(false); onCambiato() }} />}
      {rilascio && <FinestraRilascio prodotto={p} onChiudi={() => setRilascio(false)} onFatto={() => { setRilascio(false); onCambiato() }} />}
      {segnalazione && <FinestraNuovaSegnalazione prodotti={[p]} prodotto={p.id} onChiudi={() => setSegnalazione(false)} onFatto={(rid) => naviga(`/cra/reports/${rid}`)} />}
      {vex && <FinestraVex progettoId={null} prodottoCra={p.id} soggetto={vex} onChiudi={() => setVex(null)} onFatto={() => { setVex(null); onCambiato() }} />}
    </>
  )
}

function Rilasci({ p, scrive, onCambiato, onAggiungi, onProposta }: {
  p: CraProductDetail
  scrive: boolean
  onCambiato: () => void
  onAggiungi: () => void
  onProposta: (sbomId: string) => void
}) {
  const [errore, setErrore] = useState<unknown>(null)
  async function stato(r: CraRelease, s: string) {
    setErrore(null)
    try { await api.craReleaseUpdate(r.id, { status: s, supported_until: r.supported_until, notes: r.notes }); onCambiato() } catch (e) { setErrore(e) }
  }
  async function scarica(r: CraRelease) {
    setErrore(null)
    try { await scaricaJson(api.craReleaseCyclonedxPath(r.id), `${p.name}-${r.version}.cdx.json`.replace(/[^A-Za-z0-9._-]+/g, '-')) } catch (e) { setErrore(e) }
  }
  const colonne: Column<CraRelease>[] = [
    { key: 'version', header: t('Versione'), value: (r) => r.version, mono: true },
    { key: 'released_at', header: t('Uscito il'), type: 'date', value: (r) => r.released_at, render: (r) => giornoCra(r.released_at) },
    { key: 'source', header: t('Origine'), value: (r) => r.source, render: (r) => (
      <small>{r.source === 'tag' ? t('tag {r} di {p}', { r: r.ref, p: r.project }) : r.source === 'upload' ? t('a mano, con SBOM') : t('a mano, senza SBOM')}</small>) },
    { key: 'sbom', header: t('SBOM'), value: (r) => r.sbom?.components ?? -1, render: (r) => r.sbom
      ? <small>{t('{n} componenti', { n: r.sbom.components })}{r.sbom.vulnerable ? ` · ${t('{n} vulnerabili', { n: r.sbom.vulnerable })}` : ''}{r.sbom.exploited ? ` · ${t('{n} sfruttate', { n: r.sbom.exploited })}` : ''}</small>
      : <span className="badge badge--warn">{t('manca')}</span> },
    { key: 'support', header: t('Supportato fino al'), type: 'date', value: (r) => r.effective_support_until, render: (r) => giornoCra(r.effective_support_until) },
    { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => (
      <select value={r.status} disabled={!scrive} onChange={(e) => void stato(r, e.target.value)} aria-label={t('Stato del rilascio')}>
        <option value="supported">{t('supportato')}</option>
        <option value="ended">{t('supporto finito')}</option>
        <option value="withdrawn">{t('ritirato')}</option>
      </select>) },
    { key: 'azioni', header: '', filter: false, sort: false, render: (r) => r.sbom && (
      <button type="button" className="secondary" onClick={() => void scarica(r)}>{t('CycloneDX')}</button>) },
  ]
  return (
    <Card title={t('Rilasci')}>
      <ErrorBanner error={errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Ogni rilascio con il suo SBOM, conservato dieci anni o per il periodo di supporto se più lungo (art. 13(13)). Il CycloneDX scaricato qui ha anche le vulnerabilità e le valutazioni VEX; il documento originale resta quello pubblicato dalla pipeline.')}</p>
      <div className="toolbar"><button type="button" disabled={!scrive} onClick={onAggiungi}>{t('Aggiungi un rilascio')}</button></div>
      {p.proposals.length > 0 && (
        <div className="banner banner--info">
          {t('Tag con SBOM non ancora nel registro:')}
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {p.proposals.map((x) => (
              <li key={x.sbom_id}>
                <span className="mono">{x.tag}</span> · {x.project} · {t('{n} componenti', { n: x.components })}{' '}
                <button type="button" className="secondary" disabled={!scrive} onClick={() => onProposta(x.sbom_id)}>{t('Aggiungi come rilascio')}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <DataTable rows={p.release_list} rowKey={(r) => r.id} columns={colonne} conteggio={{ nome: t('rilasci') }}
        emptyText={t('Nessun rilascio ancora.')} />
    </Card>
  )
}

function TabellaVulnerabilita({ righe, scrive, onValuta, onSegnala, segnalate }: {
  righe: CraVulnerability[]
  scrive: boolean
  onValuta: (v: CraVulnerability) => void
  onSegnala: (v: CraVulnerability) => void
  segnalate: Set<string>
}) {
  const [soloSfruttate, setSoloSfruttate] = useState(true)
  const colonne: Column<CraVulnerability>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), nowrap: true, value: (v) => [v.vuln_id, ...v.aliases].join(' '), render: (v) => <span className="mono">{v.vuln_id}</span> },
    { key: 'component', header: t('Componente'), value: (v) => `${v.component} ${v.versions.join(' ')}`,
      render: (v) => <><span className="mono">{v.component}</span> <small className="text-muted">{v.versions.join(', ')}</small></> },
    { key: 'releases', header: t('Rilasci'), value: (v) => v.releases.join(' '), render: (v) => <small className="mono">{v.releases.join(', ')}</small> },
    { key: 'severity', header: t('Gravità'), value: (v) => v.severity, render: (v) => <><BadgeGravita g={gravita(v.severity)} />{' '}<FontiSfruttata fonti={v.sources} euvd={v.euvd_id} /></> },
    { key: 'fixed', header: t('Corretta in'), value: (v) => v.fixed, mono: true },
    { key: 'vex', header: t('VEX'), value: (v) => v.vex?.state ?? '', render: (v) => v.vex
      ? <small>{etichettaStatoVex(v.vex.state)} · {t('scade il {d}', { d: giornoCra(v.vex.expires_at) })}</small> : null },
    { key: 'azioni', header: '', filter: false, sort: false, render: (v) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        <button type="button" className="secondary" disabled={!scrive} onClick={() => onValuta(v)}>{t('Valuta (VEX)')}</button>{' '}
        {v.exploited && !v.vex?.excludes && (segnalate.has(`${v.vuln_id}|${v.component}`)
          ? <small className="text-muted">{t('segnalata')}</small>
          : <button type="button" disabled={!scrive} onClick={() => onSegnala(v)}>{t('Apri la segnalazione')}</button>)}
      </span>) },
  ]
  const visibili = soloSfruttate ? righe.filter((v) => v.exploited) : righe
  return (
    <>
      <div className="checkbox"><input id="solo-sfruttate" type="checkbox" checked={soloSfruttate} onChange={(e) => setSoloSfruttate(e.target.checked)} />
        <label htmlFor="solo-sfruttate">{t('Solo le sfruttate attivamente ({n} di {m})', { n: righe.filter((v) => v.exploited).length, m: righe.length })}</label></div>
      <DataTable rows={visibili} rowKey={(v) => `${v.vuln_id}|${v.component}|${v.purl_base}`} columns={colonne}
        conteggio={{ nome: t('vulnerabilità') }} emptyText={soloSfruttate ? t('Nessuna vulnerabilità sfruttata nei rilasci supportati.') : t('Nessuna vulnerabilità nei rilasci supportati.')} />
    </>
  )
}
